import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db70dihgdmoohm3rngj0";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessage("");
      return;
    }

    const email = String(formData.get("email") || "").trim();
    const content = String(formData.get("message") || "").trim();
    if (!email || !content) {
      setStatus("error");
      setFormError("请填写有效的邮箱地址与反馈内容。");
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") body.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && code === "OK") {
        setStatus("success");
        setFormError("");
        form.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg bg-background-50 border border-accent-500/50 p-6 md:p-8 text-center animate-scale-in">
        <span className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-accent-500 text-primary-950">
          <i className="ri-check-line text-2xl" />
        </span>
        <h3 className="mt-4 font-heading text-xl font-bold text-foreground-950">感谢您的反馈，稍后回复</h3>
        <p className="mt-2 text-sm text-secondary-500">我们已收到你的建议，会尽快通过邮箱与你联系。</p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-background-100 border border-background-200 text-foreground-800 text-sm hover:border-accent-500 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fb-name" className="block text-sm text-foreground-800 mb-2">
            您的称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            placeholder="请输入称呼"
            className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-secondary-600 outline-none focus:border-primary-500 transition-colors"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="block text-sm text-foreground-800 mb-2">
            邮箱地址 <span className="text-primary-400">*</span>
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-secondary-600 outline-none focus:border-primary-500 transition-colors"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-subject" className="block text-sm text-foreground-800 mb-2">
          反馈类型
        </label>
        <select
          id="fb-subject"
          name="subject"
          defaultValue="内容建议"
          className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-500 transition-colors cursor-pointer"
        >
          <option value="内容建议">内容建议</option>
          <option value="版权相关">版权相关</option>
          <option value="技术问题">技术问题</option>
          <option value="合作洽谈">合作洽谈</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="fb-message" className="block text-sm text-foreground-800">
            反馈内容 <span className="text-primary-400">*</span>
          </label>
          <span className="text-xs text-secondary-500">{message.length} / 500</span>
        </div>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 500))}
          placeholder="请描述你的问题或建议（不超过 500 字）"
          className="w-full px-3 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-secondary-600 outline-none focus:border-primary-500 transition-colors resize-none leading-relaxed"
        />
      </div>

      {/* 反垃圾字段 */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-field-optional-web"
      />

      {status === "error" && formError && (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-primary-500/10 border border-primary-500/40 px-4 py-3">
          <i className="ri-error-warning-line text-primary-400 mt-0.5" />
          <span className="text-sm text-primary-100">{formError}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin-slow" />
            提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line" />
            提交反馈
          </>
        )}
      </button>
    </form>
  );
}